import type { ReactElement } from "react";

interface IconProps {
  name: string;
  size?: number | string;
  color?: string;
  className?: string;
  decorative?: boolean;
  title?: string;
}

const PATHS: Record<string, ReactElement> = {
  home: (
    <>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5 10v10h14V10" />
    </>
  ),
  exchange: (
    <>
      <path d="M4 7h13l-3-3" />
      <path d="M20 17H7l3 3" />
    </>
  ),
  rollover: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  sika: (
    <>
      <path d="M12 3a6 6 0 0 1 6 6c0 3-2 5-3 7v3H9v-3c-1-2-3-4-3-7a6 6 0 0 1 6-6Z" />
      <path d="M10 20h4" />
    </>
  ),
  // Football / soccer ball (Sport Workspaces v1.0)
  football: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5 15.8 10.3 14.3 14.8H9.7L8.2 10.3 12 7.5Z" />
      <path d="M12 3v4.5M5 9.2l3.2 1.1M19 9.2l-3.2 1.1M7.4 19l2.3-4.2M16.6 19l-2.3-4.2" />
    </>
  ),
  // Tennis racket (Tennis Sidebar Navigation v1.0)
  tennis: (
    <>
      <ellipse cx="9" cy="9" rx="5.5" ry="6" />
      <path d="M5.4 5.2 12.6 12.4" />
      <path d="M12.6 5.2 5.4 12.4" />
      <path d="M12.9 12.9 20 20" />
    </>
  ),
  // Basketball (Sport Workspaces — basketball pilot)
  basketball: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3v18" />
      <path d="M5.6 5.6c3 2 3 10.8 0 12.8M18.4 5.6c-3 2-3 10.8 0 12.8" />
    </>
  ),
  vault: (
    <>
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <path d="M7 10v6" />
      <circle cx="14" cy="13" r="2" />
    </>
  ),
  // Globe (Sport Workspaces v1.1 — international workspace)
  international: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a13 13 0 0 1 0 18" />
      <path d="M12 3a13 13 0 0 0 0 18" />
    </>
  ),
  watch: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
    </>
  ),
  reports: (
    <>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
      <path d="M9 14h6M9 18h4" />
    </>
  ),
  audit: (
    <>
      <path d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5z" />
      <path d="M9 12h6M9 16h4" />
    </>
  ),
  knowledge: (
    <>
      <path d="M4 4h12a3 3 0 0 1 3 3v13H7a3 3 0 0 0-3 3z" />
      <path d="M4 4v16" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  bell: (
    <>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14M5 12h14" />
    </>
  ),
  chev: (
    <>
      <path d="m9 6 6 6-6 6" />
    </>
  ),
  bolt: (
    <>
      <path d="m13 2-9 12h7l-1 8 9-12h-7l1-8Z" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  star: (
    <>
      <path d="m12 2 3 7 7 .5-5.5 4.5L18 21l-6-4-6 4 1.5-7L2 9.5 9 9z" />
    </>
  ),
  arrow: (
    <>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </>
  ),
  check: (
    <>
      <path d="M5 12l4 4 10-10" />
    </>
  ),
  arrowUp: (
    <>
      <path d="m5 12 7-7 7 7" />
      <path d="M12 19V5" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v5h1" />
    </>
  ),
  refresh: (
    <>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 21v-5h5" />
    </>
  ),
  singles: (
    <>
      <path d="M4 6h16M4 12h16M4 18h10" />
      <circle cx="19" cy="18" r="2" />
    </>
  ),
  powerball: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 6v6l4 2" />
      <path d="m6 6 12 12" />
    </>
  ),
  ladder: (
    <>
      <path d="M7 3v18M17 3v18" />
      <path d="M7 7h10M7 12h10M7 17h10" />
    </>
  ),
  owl: (
    <>
      <circle cx="9" cy="11" r="2.5" />
      <circle cx="15" cy="11" r="2.5" />
      <path d="M5 8c0-3 3-5 7-5s7 2 7 5v6c0 4-3 7-7 7s-7-3-7-7Z" />
      <path d="m9 17 3 2 3-2" />
    </>
  ),
  intel: (
    <>
      <path d="M12 3a4 4 0 0 1 4 4c0 2-1 3-2 4v3h-4v-3c-1-1-2-2-2-4a4 4 0 0 1 4-4Z" />
      <path d="M10 18h4M11 21h2" />
    </>
  ),
  moon: (
    <>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </>
  ),
  close: (
    <>
      <path d="M6 6l12 12M18 6L6 18" />
    </>
  ),
  trend: (
    <>
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M14 7h7v7" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="9" r="3.5" />
      <path d="M2 20c0-3.5 3-6 7-6s7 2.5 7 6" />
      <circle cx="17" cy="8" r="3" />
      <path d="M22 19c0-2.8-2-5-5-5" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4 2 20h20Z" />
      <path d="M12 10v5M12 18v.01" />
    </>
  ),
  dna: (
    <>
      <path d="M5 4c4 0 4 16 0 16M19 4c-4 0-4 16 0 16" />
      <path d="M7 7h10M7 12h10M7 17h10" />
    </>
  ),
  trail: (
    <>
      <path d="M5 5h14v14H5z" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="5" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </>
  ),
  menu: (
    <>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <path d="M12 17v4M8 21h8" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 6v6l4 2" />
    </>
  ),
  // Phase SIKA-5g — speech bubble for "Conversations" nav row.
  bubble: (
    <>
      <path d="M21 12a8 8 0 0 1-12.5 6.6L4 20l1.4-4.5A8 8 0 1 1 21 12z" />
    </>
  ),
  // Phase SIKA-5g — chart line for "Active plan" nav row.
  chart: (
    <>
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M14 7h7v7" />
    </>
  ),
  // Phase SIKA-5g — shield for "Vault" nav row.
  shield: (
    <>
      <path d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4z" />
    </>
  ),
  // Phase SE-1 — Slip Extractor sidebar icon (per spec §6.1).
  // Tapered funnel narrowing to a refined drop — the visual verb for
  // filtering a slip down to its highest-value subset.
  funnel: (
    <>
      <path d="M4 4h16l-6 8v6l-4 2v-8z" />
      <circle cx="12" cy="20" r="1.5" />
    </>
  ),
  // Phase SIKA-5g — chevron variants (the existing `chev` points
  // right; provide explicit aliases so sidebars can pick by name).
  "chev-right": (
    <>
      <path d="m9 6 6 6-6 6" />
    </>
  ),
  "chev-left": (
    <>
      <path d="m15 6-6 6 6 6" />
    </>
  ),
  // Hardwood Gauntlet — a flame (the "flaming basketball" Gauntlet mark).
  flame: (
    <>
      <path d="M12 3c1 3 4 4.5 4 8a4 4 0 0 1-8 0c0-1.6.8-2.6 1.5-3.5C10 9 11.5 6 12 3Z" />
      <path d="M12 21a4 4 0 0 0 4-4c0-2-2-3-4-6-2 3-4 4-4 6a4 4 0 0 0 4 4Z" />
    </>
  ),
};

export function Icon({ name, size = 16, color, className }: IconProps): JSX.Element {
  const s = typeof size === "string" ? Number(size) || 16 : size;
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color ?? "currentColor"}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className ?? "icon"}
    >
      {PATHS[name] ?? null}
    </svg>
  );
}
